import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { smoothScrollHash } from "../lib/smoothScroll";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative border-b border-black/10 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14 sm:pt-24 sm:pb-20">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <div
              className="inline-flex items-center gap-2 border border-black/15 px-3 py-1.5 mb-6 reveal"
              data-testid="hero-badge"
            >
              <Sparkles size={14} className="text-[#EAB308]" />
              <span className="text-xs font-semibold tracking-widest uppercase">
                For India&apos;s Founders, NRIs &amp; HNIs
              </span>
            </div>

            <h1
              className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight reveal"
              style={{ animationDelay: "60ms" }}
              data-testid="hero-headline"
            >
              Everything a new{" "}
              <span className="text-[#EAB308]">founder</span> needs.
              <br />
              In one place.
            </h1>

            <p
              className="mt-6 text-lg sm:text-xl leading-relaxed text-black/70 max-w-2xl reveal"
              style={{ animationDelay: "140ms" }}
              data-testid="hero-subheading"
            >
              Startup Funding, Company Registration, Banking, Payment
              Gateways, Office Workspace, IT Hardware, Branding and Digital
              Presence — delivered by Experienced Team of Industry Experts so
              you can focus on building.
            </p>

            <div
              className="mt-8 flex flex-wrap items-center gap-3 reveal"
              style={{ animationDelay: "220ms" }}
            >
              <a
                href="#lead-form"
                onClick={smoothScrollHash}
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-2 bg-[#EAB308] text-white px-6 py-3.5 font-semibold hover:bg-black transition-colors"
              >
                Talk to an expert
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href="#services"
                onClick={smoothScrollHash}
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 border border-black px-6 py-3.5 font-semibold hover:bg-black hover:text-white transition-colors"
              >
                Browse services
              </a>
            </div>

            <p
              className="mt-5 text-base sm:text-lg leading-relaxed text-black/80 reveal"
              style={{ animationDelay: "260ms" }}
              data-testid="hero-name-check-link"
            >
              <Link
                to="/name-check"
                className="font-bold underline decoration-[#EAB308] decoration-2 underline-offset-4 hover:text-[#EAB308] transition-colors"
              >
                Check Company Name Availability
              </Link>{" "}
              — Instant <b>Startup Name Availability Search</b> on MCA, Domains
              &amp; Trademark.
            </p>

            <div
              className="mt-10 grid grid-cols-3 gap-6 max-w-xl reveal"
              style={{ animationDelay: "300ms" }}
            >
              <Stat value="1,200+" label="Founders served" testId="stat-founders" />
              <Stat value="7 days" label="Avg. incorporation" testId="stat-days" />
              <Stat value="Pan-India" label="Expert network" testId="stat-network" />
            </div>
          </div>

          <div
            className="col-span-12 lg:col-span-4 hidden lg:block"
            data-testid="hero-side-panel"
          >
            <div className="border border-black p-6 bg-[#F4F4F5]">
              <p className="font-heading text-xs tracking-[0.22em] uppercase text-black/60">
                Starter Bundle
              </p>
              <p className="font-heading font-black text-3xl mt-2 leading-tight">
                Launch-ready in under 2 weeks.
              </p>
              <ul className="mt-5 space-y-3 text-sm">
                {[
                  "Company or LLP Registration with PAN and TAN",
                  "Current Account + Payment Gateway",
                  "Website + Business Email",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <span className="mt-1 inline-block w-2 h-2 bg-[#EAB308]" />
                    <span className="text-black/80">{t}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#lead-form"
                onClick={smoothScrollHash}
                data-testid="hero-bundle-cta"
                className="mt-6 inline-flex w-full justify-center items-center gap-2 bg-[#EAB308] text-white px-4 py-3 font-semibold hover:bg-black transition-colors"
              >
                Get the bundle →
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-black/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="relative mx-auto"
            style={{ aspectRatio: "16 / 7" }}
            data-testid="hero-image-wrapper"
          >
            <img
              src="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600"
              alt="Indian founders and professionals collaborating in a modern office"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-black/10" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label, testId }) {
  return (
    <div data-testid={testId}>
      <div className="font-heading font-black text-2xl sm:text-3xl">{value}</div>
      <div className="text-xs sm:text-sm text-black/60 mt-1">{label}</div>
    </div>
  );
}
